
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuoteList } from '../../hooks/useQuoteList';
import QuoteListWidget from '../../components/QuoteListWidget';

interface Mattress {
  id: string;
  name: string;
  description: string;
  feel: string;
  image: string;
  alt: string;
  brand: string;
  collection: string;
  size: string;
  sizes: string[];
}

interface Collection {
  name: string;
  mattresses: Mattress[];
}

interface BrandData {
  name: string;
  collections: Collection[];
}

const BrandPage = () => {
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const navigate = useNavigate();
  const { addToList, removeFromList, isInList, list } = useQuoteList();
  const [showCallConfirm, setShowCallConfirm] = useState(false);

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCallConfirm(true);
  };

  const confirmCall = () => {
    setShowCallConfirm(false);
    window.location.href = 'tel:+19177577737';
  };

  const brandData: Record<string, BrandData> = {
    'stearns-foster': {
      name: 'Stearns & Foster',
      collections: [
        {
          name: 'Studio Collection',
          mattresses: [
            {
              id: 'sf-studio-twin',
              name: 'Studio Twin',
              description: 'Balanced medium feel with quality construction and reliable comfort.',
              feel: 'Medium comfort with quality construction',
              image: 'https://public.readdy.ai/ai/img_res/ad9bbcc2c724d3d384fc9d6832f3a592.jpg',
              alt: 'Stearns & Foster Studio Twin mattress',
              brand: 'Stearns & Foster',
              collection: 'Studio',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sf-studio-full',
              name: 'Studio Full',
              description: 'Balanced medium feel with quality construction and reliable comfort.',
              feel: 'Medium comfort with quality construction',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Studio%20full%20mattress%20complete%20full%20product%20view%20balanced%20construction%20quality%20materials%20medium%20comfort%20design%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_studio_full_new&orientation=landscape',
              alt: 'Stearns & Foster Studio Full mattress',
              brand: 'Stearns & Foster',
              collection: 'Studio',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'Estate Collection',
          mattresses: [
            {
              id: 'sf-estate-twin',
              name: 'Estate Twin',
              description: 'Medium-firm feel with traditional innerspring support and premium materials.',
              feel: 'Luxury firm with traditional innerspring support',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Estate%20twin%20mattress%20complete%20full%20product%20view%20premium%20innerspring%20construction%20visible%20elegant%20quilted%20top%20pattern%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_estate_twin_new&orientation=landscape',
              alt: 'Stearns & Foster Estate Twin mattress',
              brand: 'Stearns & Foster',
              collection: 'Estate',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sf-estate-queen',
              name: 'Estate Queen',
              description: 'Medium-firm feel with traditional innerspring support and premium materials.',
              feel: 'Luxury firm with traditional innerspring support',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Estate%20queen%20mattress%20complete%20full%20product%20view%20premium%20innerspring%20construction%20visible%20elegant%20quilted%20top%20pattern%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_estate_queen_new&orientation=landscape',
              alt: 'Stearns & Foster Estate Queen mattress',
              brand: 'Stearns & Foster',
              collection: 'Estate',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sf-estate-king',
              name: 'Estate King',
              description: 'Medium-firm feel with traditional innerspring support and premium materials.',
              feel: 'Luxury firm with traditional innerspring support',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Estate%20king%20mattress%20complete%20full%20product%20view%20premium%20innerspring%20construction%20visible%20elegant%20quilted%20top%20pattern%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_estate_king_new&orientation=landscape',
              alt: 'Stearns & Foster Estate King mattress',
              brand: 'Stearns & Foster',
              collection: 'Estate',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'Lux Estate Collection',
          mattresses: [
            {
              id: 'sf-lux-queen',
              name: 'Lux Estate Queen',
              description: 'Ultra-luxurious firm feel with silk and cashmere materials.',
              feel: 'Ultra-luxurious firm with silk and cashmere materials',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Lux%20Estate%20queen%20mattress%20complete%20full%20product%20view%20ultra-premium%20materials%20visible%20luxury%20construction%20details%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_lux_queen_new&orientation=landscape',
              alt: 'Stearns & Foster Lux Estate Queen mattress',
              brand: 'Stearns & Foster',
              collection: 'Lux Estate',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sf-lux-king',
              name: 'Lux Estate King',
              description: 'Ultra-luxurious firm feel with silk and cashmere materials.',
              feel: 'Ultra-luxurious firm with silk and cashmere materials',
              image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20Lux%20Estate%20king%20mattress%20complete%20full%20product%20view%20ultra-premium%20materials%20visible%20luxury%20construction%20details%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sf_lux_king_new&orientation=landscape',
              alt: 'Stearns & Foster Lux Estate King mattress',
              brand: 'Stearns & Foster',
              collection: 'Lux Estate',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        }
      ]
    },
    'serta': {
      name: 'Serta',
      collections: [
        {
          name: 'Perfect Sleeper',
          mattresses: [
            {
              id: 'serta-ps-twin',
              name: 'Perfect Sleeper Twin',
              description: 'Medium-firm feel with cooling gel memory foam addressing common sleep problems.',
              feel: 'Medium-firm with cooling gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20Perfect%20Sleeper%20twin%20mattress%20complete%20full%20product%20view%20cooling%20gel%20memory%20foam%20layers%20visible%20medium-firm%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_ps_twin_new&orientation=landscape',
              alt: 'Serta Perfect Sleeper Twin mattress',
              brand: 'Serta',
              collection: 'Perfect Sleeper',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-ps-full',
              name: 'Perfect Sleeper Full',
              description: 'Medium-firm feel with cooling gel memory foam addressing common sleep problems.',
              feel: 'Medium-firm with cooling gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20Perfect%20Sleeper%20full%20mattress%20complete%20full%20product%20view%20cooling%20gel%20memory%20foam%20layers%20visible%20medium-firm%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_ps_full_new&orientation=landscape',
              alt: 'Serta Perfect Sleeper Full mattress',
              brand: 'Serta',
              collection: 'Perfect Sleeper',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-ps-queen',
              name: 'Perfect Sleeper Queen',
              description: 'Medium-firm feel with cooling gel memory foam addressing common sleep problems.',
              feel: 'Medium-firm with cooling gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20Perfect%20Sleeper%20queen%20mattress%20complete%20full%20product%20view%20cooling%20gel%20memory%20foam%20layers%20visible%20medium-firm%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_ps_queen_new&orientation=landscape',
              alt: 'Serta Perfect Sleeper Queen mattress',
              brand: 'Serta',
              collection: 'Perfect Sleeper',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-ps-king',
              name: 'Perfect Sleeper King',
              description: 'Medium-firm feel with cooling gel memory foam addressing common sleep problems.',
              feel: 'Medium-firm with cooling gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20Perfect%20Sleeper%20king%20mattress%20complete%20full%20product%20view%20cooling%20gel%20memory%20foam%20layers%20visible%20medium-firm%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_ps_king_new&orientation=landscape',
              alt: 'Serta Perfect Sleeper King mattress',
              brand: 'Serta',
              collection: 'Perfect Sleeper',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'iSeries Hybrid',
          mattresses: [
            {
              id: 'serta-iseries-twin',
              name: 'iSeries Hybrid Twin',
              description: 'Hybrid construction combining memory foam comfort with innerspring responsiveness.',
              feel: 'Firm hybrid with memory foam and innerspring',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iSeries%20Hybrid%20twin%20mattress%20complete%20full%20product%20view%20hybrid%20construction%20layers%20visible%20memory%20foam%20and%20coils%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_iseries_twin_new&orientation=landscape',
              alt: 'Serta iSeries Hybrid Twin mattress',
              brand: 'Serta',
              collection: 'iSeries Hybrid',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-iseries-full',
              name: 'iSeries Hybrid Full',
              description: 'Hybrid construction combining memory foam comfort with innerspring responsiveness.',
              feel: 'Firm hybrid with memory foam and innerspring',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iSeries%20Hybrid%20full%20mattress%20complete%20full%20product%20view%20hybrid%20construction%20layers%20visible%20memory%20foam%20and%20coils%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_iseries_full_new&orientation=landscape',
              alt: 'Serta iSeries Hybrid Full mattress',
              brand: 'Serta',
              collection: 'iSeries Hybrid',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-iseries-queen',
              name: 'iSeries Hybrid Queen',
              description: 'Hybrid construction combining memory foam comfort with innerspring responsiveness.',
              feel: 'Firm hybrid with memory foam and innerspring',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iSeries%20Hybrid%20queen%20mattress%20complete%20full%20product%20view%20hybrid%20construction%20layers%20visible%20memory%20foam%20and%20coils%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_iseries_queen_new&orientation=landscape',
              alt: 'Serta iSeries Hybrid Queen mattress',
              brand: 'Serta',
              collection: 'iSeries Hybrid',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-iseries-king',
              name: 'iSeries Hybrid King',
              description: 'Hybrid construction combining memory foam comfort with innerspring responsiveness.',
              feel: 'Firm hybrid with memory foam and innerspring',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iSeries%20Hybrid%20king%20mattress%20complete%20full%20product%20view%20hybrid%20construction%20layers%20visible%20memory%20foam%20and%20coils%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_iseries_king_new&orientation=landscape',
              alt: 'Serta iSeries Hybrid King mattress',
              brand: 'Serta',
              collection: 'iSeries Hybrid',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'iComfort',
          mattresses: [
            {
              id: 'serta-icomfort-twin',
              name: 'iComfort Twin',
              description: 'Medium feel with TempActiv gel memory foam providing advanced cooling.',
              feel: 'Medium cooling with TempActiv gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iComfort%20twin%20mattress%20complete%20full%20product%20view%20TempActiv%20gel%20memory%20foam%20layers%20visible%20cooling%20technology%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_icomfort_twin_new&orientation=landscape',
              alt: 'Serta iComfort Twin mattress',
              brand: 'Serta',
              collection: 'iComfort',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-icomfort-full',
              name: 'iComfort Full',
              description: 'Medium feel with TempActiv gel memory foam providing advanced cooling.',
              feel: 'Medium cooling with TempActiv gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iComfort%20full%20mattress%20complete%20full%20product%20view%20TempActiv%20gel%20memory%20foam%20layers%20visible%20cooling%20technology%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_icomfort_full_new&orientation=landscape',
              alt: 'Serta iComfort Full mattress',
              brand: 'Serta',
              collection: 'iComfort',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-icomfort-queen',
              name: 'iComfort Queen',
              description: 'Medium feel with TempActiv gel memory foam providing advanced cooling.',
              feel: 'Medium cooling with TempActiv gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iComfort%20queen%20mattress%20complete%20full%20product%20view%20TempActiv%20gel%20memory%20foam%20layers%20visible%20cooling%20technology%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_icomfort_queen_new&orientation=landscape',
              alt: 'Serta iComfort Queen mattress',
              brand: 'Serta',
              collection: 'iComfort',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'serta-icomfort-king',
              name: 'iComfort King',
              description: 'Medium feel with TempActiv gel memory foam providing advanced cooling.',
              feel: 'Medium cooling with TempActiv gel memory foam',
              image: 'https://readdy.ai/api/search-image?query=Serta%20iComfort%20king%20mattress%20complete%20full%20product%20view%20TempActiv%20gel%20memory%20foam%20layers%20visible%20cooling%20technology%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=serta_icomfort_king_new&orientation=landscape',
              alt: 'Serta iComfort King mattress',
              brand: 'Serta',
              collection: 'iComfort',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        }
      ]
    },
    'purple': {
      name: 'Purple',
      collections: [
        {
          name: 'Rejuvenate Lux Collection',
          mattresses: [
            {
              id: 'purple-rejuvenate-queen',
              name: 'Rejuvenate Lux Queen',
              description: 'Plush comfort with premium gel grid technology and luxury materials.',
              feel: 'Plush comfort with premium gel grid technology',
              image: 'https://readdy.ai/api/search-image?query=Purple%20Rejuvenate%20Lux%20queen%20mattress%20complete%20full%20product%20view%20premium%20gel%20grid%20technology%20visible%20luxury%20plush%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=purple_rejuvenate_queen_new&orientation=landscape',
              alt: 'Purple Rejuvenate Lux Queen mattress',
              brand: 'Purple',
              collection: 'Rejuvenate Lux',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'purple-rejuvenate-king',
              name: 'Rejuvenate Lux King',
              description: 'Plush comfort with premium gel grid technology and luxury materials.',
              feel: 'Plush comfort with premium gel grid technology',
              image: 'https://readdy.ai/api/search-image?query=Purple%20Rejuvenate%20Lux%20king%20mattress%20complete%20full%20product%20view%20premium%20gel%20grid%20technology%20visible%20luxury%20plush%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=purple_rejuvenate_king_new&orientation=landscape',
              alt: 'Purple Rejuvenate Lux King mattress',
              brand: 'Purple',
              collection: 'Rejuvenate Lux',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        }
      ]
    },
    'beautyrest': {
      name: 'Beautyrest',
      collections: [
        {
          name: 'Harmony Lux Collection',
          mattresses: [
            {
              id: 'beauty-harmony-full',
              name: 'Harmony Lux Full',
              description: 'Medium feel with eco-friendly materials and sustainable construction.',
              feel: 'Medium comfort with eco-friendly materials',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Harmony%20Lux%20full%20mattress%20complete%20full%20product%20view%20eco-friendly%20materials%20visible%20sustainable%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_harmony_full_new&orientation=landscape',
              alt: 'Beautyrest Harmony Lux Full mattress',
              brand: 'Beautyrest',
              collection: 'Harmony Lux',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'Black Collection',
          mattresses: [
            {
              id: 'beauty-black-full',
              name: 'Black Full',
              description: 'Extra firm feel with premium pocketed coil technology and luxury materials.',
              feel: 'Extra firm with premium pocketed coil technology',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Black%20full%20mattress%20complete%20full%20product%20view%20premium%20pocketed%20coil%20construction%20visible%20luxury%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_black_full_new&orientation=landscape',
              alt: 'Beautyrest Black Full mattress',
              brand: 'Beautyrest',
              collection: 'Black',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'beauty-black-queen',
              name: 'Black Queen',
              description: 'Extra firm feel with premium pocketed coil technology and luxury materials.',
              feel: 'Extra firm with premium pocketed coil technology',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Black%20queen%20mattress%20complete%20full%20product%20view%20premium%20pocketed%20coil%20construction%20visible%20luxury%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_black_queen_new&orientation=landscape',
              alt: 'Beautyrest Black Queen mattress',
              brand: 'Beautyrest',
              collection: 'Black',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'beauty-black-king',
              name: 'Black King',
              description: 'Extra firm feel with premium pocketed coil technology and luxury materials.',
              feel: 'Extra firm with premium pocketed coil technology',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Black%20king%20mattress%20complete%20full%20product%20view%20premium%20pocketed%20coil%20construction%20visible%20luxury%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_black_king_new&orientation=landscape',
              alt: 'Beautyrest Black King mattress',
              brand: 'Beautyrest',
              collection: 'Black',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'Diamond Collection',
          mattresses: [
            {
              id: 'beauty-diamond-queen',
              name: 'Diamond Queen',
              description: 'Medium comfort with advanced pocketed coil technology providing excellent motion isolation.',
              feel: 'Medium comfort with advanced pocketed coil technology',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Diamond%20queen%20mattress%20complete%20full%20product%20view%20advanced%20pocketed%20coils%20visible%20medium%20comfort%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_diamond_queen_new&orientation=landscape',
              alt: 'Beautyrest Diamond Queen mattress',
              brand: 'Beautyrest',
              collection: 'Diamond',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'beauty-diamond-king',
              name: 'Diamond King',
              description: 'Medium comfort with advanced pocketed coil technology providing excellent motion isolation.',
              feel: 'Medium comfort with advanced pocketed coil technology',
              image: 'https://readdy.ai/api/search-image?query=Beautyrest%20Diamond%20king%20mattress%20complete%20full%20product%20view%20advanced%20pocketed%20coils%20visible%20medium%20comfort%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=beauty_diamond_king_new&orientation=landscape',
              alt: 'Beautyrest Diamond King mattress',
              brand: 'Beautyrest',
              collection: 'Diamond',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        }
      ]
    },
    'sealy': {
      name: 'Sealy',
      collections: [
        {
          name: 'Posturepedic',
          mattresses: [
            {
              id: 'sealy-posture-twin',
              name: 'Posturepedic Twin',
              description: 'Classic Posturepedic support with firm feel for proper spinal alignment.',
              feel: 'Firm support with classic Posturepedic technology',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20twin%20mattress%20complete%20full%20product%20view%20classic%20Posturepedic%20technology%20firm%20support%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_posture_twin_new&orientation=landscape',
              alt: 'Sealy Posturepedic Twin mattress',
              brand: 'Sealy',
              collection: 'Posturepedic',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-posture-full',
              name: 'Posturepedic Full',
              description: 'Classic Posturepedic support with firm feel for proper spinal alignment.',
              feel: 'Firm support with classic Posturepedic technology',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20full%20mattress%20complete%20full%20product%20view%20classic%20Posturepedic%20technology%20firm%20support%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_posture_full_new&orientation=landscape',
              alt: 'Sealy Posturepedic Full mattress',
              brand: 'Sealy',
              collection: 'Posturepedic',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-posture-queen',
              name: 'Posturepedic Queen',
              description: 'Classic Posturepedic support with firm feel for proper spinal alignment.',
              feel: 'Firm support with classic Posturepedic technology',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20queen%20mattress%20complete%20full%20product%20view%20classic%20Posturepedic%20technology%20firm%20support%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_posture_queen_new&orientation=landscape',
              alt: 'Sealy Posturepedic Queen mattress',
              brand: 'Sealy',
              collection: 'Posturepedic',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-posture-king',
              name: 'Posturepedic King',
              description: 'Classic Posturepedic support with firm feel for proper spinal alignment.',
              feel: 'Firm support with classic Posturepedic technology',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20king%20mattress%20complete%20full%20product%20view%20classic%20Posturepedic%20technology%20firm%20support%20construction%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_posture_king_new&orientation=landscape',
              alt: 'Sealy Posturepedic King mattress',
              brand: 'Sealy',
              collection: 'Posturepedic',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        },
        {
          name: 'Posturepedic Plus',
          mattresses: [
            {
              id: 'sealy-plus-twin',
              name: 'Posturepedic Plus Twin',
              description: 'Firm support with Posturepedic technology providing targeted reinforcement and cooling comfort.',
              feel: 'Firm support with targeted reinforcement and cooling',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20Plus%20twin%20mattress%20complete%20full%20product%20view%20Posturepedic%20coil%20technology%20visible%20cooling%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_plus_twin_new&orientation=landscape',
              alt: 'Sealy Posturepedic Plus Twin mattress',
              brand: 'Sealy',
              collection: 'Posturepedic Plus',
              size: 'Twin',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-plus-full',
              name: 'Posturepedic Plus Full',
              description: 'Firm support with Posturepedic technology providing targeted reinforcement and cooling comfort.',
              feel: 'Firm support with targeted reinforcement and cooling',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20Plus%20full%20mattress%20complete%20full%20product%20view%20Posturepedic%20coil%20technology%20visible%20cooling%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_plus_full_new&orientation=landscape',
              alt: 'Sealy Posturepedic Plus Full mattress',
              brand: 'Sealy',
              collection: 'Posturepedic Plus',
              size: 'Full',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-plus-queen',
              name: 'Posturepedic Plus Queen',
              description: 'Firm support with Posturepedic technology providing targeted reinforcement and cooling comfort.',
              feel: 'Firm support with targeted reinforcement and cooling',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20Plus%20queen%20mattress%20complete%20full%20product%20view%20Posturepedic%20coil%20technology%20visible%20cooling%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_plus_queen_new&orientation=landscape',
              alt: 'Sealy Posturepedic Plus Queen mattress',
              brand: 'Sealy',
              collection: 'Posturepedic Plus',
              size: 'Queen',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            },
            {
              id: 'sealy-plus-king',
              name: 'Posturepedic Plus King',
              description: 'Firm support with Posturepedic technology providing targeted reinforcement and cooling comfort.',
              feel: 'Firm support with targeted reinforcement and cooling',
              image: 'https://readdy.ai/api/search-image?query=Sealy%20Posturepedic%20Plus%20king%20mattress%20complete%20full%20product%20view%20Posturepedic%20coil%20technology%20visible%20cooling%20materials%20white%20background%20professional%20product%20photography%20entire%20mattress%20shown%20no%20people&width=800&height=600&seq=sealy_plus_king_new&orientation=landscape',
              alt: 'Sealy Posturepedic Plus King mattress',
              brand: 'Sealy',
              collection: 'Posturepedic Plus',
              size: 'King',
              sizes: ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Cal King']
            }
          ]
        }
      ]
    }
  };

  const currentBrand = brandSlug ? brandData[brandSlug] : null;

  if (!currentBrand) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Brand not found</h2>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const ContactBlock = () => (
    <div className="bg-cyan-50 rounded-lg p-4 border-l-4 border-cyan-600 mt-4">
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <span className="text-lg">📧</span>
          <a 
            href="mailto:joevgindi@gmail.com" 
            className="hover:text-cyan-600 transition-colors cursor-pointer text-sm font-medium"
            style={{ color: '#222' }}
          >
            joevgindi@gmail.com
          </a>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-lg">📞</span>
          <button 
            onClick={handlePhoneClick}
            className="hover:text-cyan-600 transition-colors cursor-pointer text-sm font-medium"
            style={{ color: '#222' }}
          >
            917-757-7737
          </button>
        </div>
      </div>
    </div>
  );

  const handleToggleList = (mattress: Mattress) => {
    if (isInList(mattress.id)) {
      removeFromList(mattress.id);
    } else {
      addToList(mattress);
    }
  };

  const getItemQuantity = (mattressId: string) => {
    const item = list.find(item => item.id === mattressId);
    return item ? item.quantity : 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-800" style={{ color: '#222' }}>
      {/* Call Confirmation Modal */}
      {showCallConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Make a Call?
              </h3>
              <p className="text-gray-600 mb-6">
                Would you like to make a call to Midnight Mattresses?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCallConfirm(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCall}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Call Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote List Widget */}
      <QuoteListWidget />

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className="cursor-pointer mr-4"
              >
                <img 
                  src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
                  alt="Midnight Mattresses Logo"
                  className="w-16 h-16"
                />
              </button>
              <h1 className="text-2xl font-bold text-slate-800">
                Midnight Mattresses
              </h1>
            </div>
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="text-slate-600 hover:text-cyan-600 transition-colors cursor-pointer font-medium"
              >
                ← Back to Home
              </button>
              <div className="flex items-center space-x-2 text-slate-600">
                <i className="ri-mail-line text-lg text-cyan-600"></i>
                <span className="text-sm font-medium">joevgindi@gmail.com</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-600">
                <i className="ri-phone-line text-lg text-cyan-600"></i>
                <button
                  onClick={handlePhoneClick}
                  className="text-sm font-medium hover:text-cyan-600 transition-colors cursor-pointer"
                >
                  917-757-7737
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl font-bold text-white mb-4">
            {currentBrand.name}
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            Explore all current {currentBrand.name} collections and find your perfect sleep solution.
          </p>
        </div>
      </section>

      {/* Collections */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {currentBrand.collections.map((collection, collectionIndex) => (
            <div key={collectionIndex} className="mb-16">
              <h3 className="text-3xl font-bold text-white mb-8 text-center">
                {collection.name}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {collection.mattresses.map((mattress) => {
                  const quantity = getItemQuantity(mattress.id);
                  const isInCart = isInList(mattress.id);
                  
                  return (
                    <article key={mattress.id} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105" data-product-shop>
                      <figure className="aspect-w-4 aspect-h-3">
                        <img 
                          src={mattress.image}
                          alt={mattress.alt}
                          className="w-full h-64 object-cover object-center"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/800x600/f3f4f6/6b7280?text=Mattress+Image';
                          }}
                        />
                      </figure>
                      <div className="p-6">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          {mattress.name}
                        </h4>
                        <p className="text-gray-700 mb-3">
                          {mattress.feel}
                        </p>
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-gray-900 mb-2">Available Sizes:</p>
                          <div className="flex flex-wrap gap-1">
                            {mattress.sizes.map((size) => (
                              <span key={size} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                {size}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleToggleList(mattress)}
                          className={`w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 mb-4 whitespace-nowrap cursor-pointer ${
                            isInCart
                              ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                              : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                          }`}
                        >
                          {isInCart ? `Remove from List ${quantity > 0 ? `(${quantity})` : ''}` : 'Add to My List'}
                        </button>
                        
                        <ContactBlock />
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/90 backdrop-blur-sm text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
                  alt="Midnight Mattresses Logo"
                  className="w-10 h-10 mr-3"
                />
                <h4 className="text-xl font-bold text-cyan-400">
                  Midnight Mattresses
                </h4>
              </div>
              <p className="text-slate-300">
                Premium mattresses with personal service throughout New Jersey and New York.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-cyan-400">Contact Information</h5>
              <div className="space-y-2 text-slate-300">
                <div className="flex items-center space-x-2">
                  <i className="ri-mail-line text-cyan-400"></i>
                  <span>joevgindi@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-phone-line text-cyan-400"></i>
                  <span>917-757-7737</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-cyan-400"></i>
                  <span>New Jersey / New York</span>
                </div>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-cyan-400">Owners</h5>
              <div className="text-slate-300 space-y-1">
                <p>Joe V Gindi</p>
                <p>Leor Edri</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 Midnight Mattresses LLC. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-cyan-400 hover:text-cyan-300 ml-1 cursor-pointer">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BrandPage;
